import React, { useMemo, useState } from "react";
import { Check, ChevronsUpDown } from "lucide-react";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "../components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { usePrescribedTherapyOptions } from "../stores/MasterTables/masterTables.queries";
import {
  filterPrescribedTherapyOptions,
  prescribedTherapyInputValue,
} from "../helpers/prescribedTherapyMatch";
import type { PrescribedTherapyOptionDto, TherapyType } from "../api-client";

export interface PrescribedTherapySelectProps {
  /** Current text — a shortLabel, an alias, or free text the doctor wrote. */
  value?: string;
  /** Emits the text to store as rawText; the server resolves it to a term. */
  onChange: (value: string) => void;
  /** Discipline filter. Terms valid in both are always offered. */
  therapyType?: TherapyType | string;
  /**
   * Show supplementary terms (Wärme, Kälte) instead of main ones.
   *
   * `null` offers BOTH classes, and has to be passed deliberately — it is for a
   * field whose row can hold either, such as the Rx table's single Heilmittel
   * column. `undefined` still means main-only (see the default below); that
   * distinction is the whole point, because "no filter" arrived by accident
   * once and put Voll-/Teilbad at the top of every main therapy cell.
   */
  isSupplementary?: boolean | null;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
  /** Rendered under the field when the term could not be recognised. */
  name?: string;
}

/**
 * Heilmittel input for the Rx forms: pick a prescribed TERM.
 *
 * Prescriptions name a term ("KG-ZNS", "Bobath", "Wärme"), never a billable
 * line item — the line item is payer- and credential-bound and is resolved per
 * slot at scheduling (MD-232). The old MUI field offered tarif-filtered line
 * items and cleared anything not on that list, which is why a perfectly valid
 * "KG-ZNS" could not be entered.
 *
 * Three ways in, per requirement:
 *  - type and autocomplete, matching labels AND aliases (so "Bobath", "Fango"
 *    or BG position "8103" all find their term);
 *  - open it and scroll the whole catalog, choosing nothing by typing;
 *  - type something the catalog does not know — it is kept verbatim as
 *    rawText. The server resolves text→alias→term on save and returns a
 *    precise error if it cannot, which is the same path scanned/AI-ingested
 *    prescriptions take.
 *
 * shadcn Command + Popover rather than MUI Autocomplete: this is the direction
 * of travel, and it is also what makes free text and alias matching
 * straightforward instead of a fight with MUI's option model.
 */
export const PrescribedTherapySelect: React.FC<PrescribedTherapySelectProps> = ({
  value = "",
  onChange,
  therapyType,
  // Defaults to false — a field that does not declare itself supplementary is
  // a MAIN Heilmittel field. Previously this defaulted to undefined, which the
  // scoping below read as "no filter", so the 12 main-therapy call sites
  // offered supplementary-only terms as well. The catalog sorts "BAD"
  // (Voll-/Teilbad, isSupplementary) first, so the first option in every main
  // therapy cell was one that cannot be a main prescription line — the server
  // then rejected the save with RX_THERAPY_MAPPING_FAILED. The 4 supplementary
  // cells pass `isSupplementary` explicitly and are unaffected.
  isSupplementary = false,
  disabled,
  placeholder = "Heilmittel",
  className,
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const { data: options = [] } = usePrescribedTherapyOptions(therapyType as TherapyType);

  const scoped = useMemo(
    () =>
      isSupplementary === null
        ? options
        : options.filter((o) => Boolean(o.isSupplementary) === isSupplementary),
    [options, isSupplementary]
  );

  const shown = useMemo(
    () => filterPrescribedTherapyOptions(scoped, query),
    [scoped, query]
  );

  // Whether the current text is something the server will recognise. Purely
  // advisory — unknown text is still allowed through, because rawText is the
  // point; it just gets flagged so a typo is visible before saving.
  const isRecognised = useMemo(
    () => !value || filterPrescribedTherapyOptions(scoped, value).length > 0,
    [scoped, value]
  );

  const commit = (option: PrescribedTherapyOptionDto) => {
    onChange(prescribedTherapyInputValue(option));
    setQuery("");
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild disabled={disabled}>
        <button
          type="button"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          title={value || placeholder}
          className={[
            "flex h-7 w-full items-center justify-between gap-1 rounded-control",
            "border border-solid bg-card px-2 text-dense text-ink-text",
            "disabled:cursor-not-allowed disabled:opacity-60",
            isRecognised ? "border-ink-edge" : "border-warning",
            className ?? "",
          ].join(" ")}
        >
          <span className="truncate">
            {value || <span className="text-ink-muted">{placeholder}</span>}
          </span>
          <ChevronsUpDown className="h-3 w-3 shrink-0 opacity-50" aria-hidden />
        </button>
      </PopoverTrigger>
      {/*
        z-[1400]: every Rx form renders this field inside a MUI Dialog, whose
        modal layer is z-index 1300. PopoverContent portals to document.body
        with the shadcn default of z-50, so the list painted UNDER the dialog
        and the dialog's presentation container swallowed every click on an
        option — the field looked fine and simply could not be picked from.
        1400 is the same value CalendarContextMenu and PatientBillDetailsModalShad
        already use to clear MUI's modal layer.
      */}
      <PopoverContent className="z-[1400] w-[320px] p-0" align="start">
        {/*
          shouldFilter={false}: cmdk's built-in scoring only sees the rendered
          value, so it cannot match on aliases. Filtering and ranking happen in
          filterPrescribedTherapyOptions, which mirrors the backend's
          normalization exactly.
        */}
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Heilmittel oder Kürzel…"
            value={query}
            onValueChange={setQuery}
          />
          <CommandList>
            <CommandEmpty>
              <div className="px-2 py-1.5 text-dense text-ink-muted">
                Kein Katalogeintrag. Text wird unverändert übernommen.
              </div>
            </CommandEmpty>
            <CommandGroup>
              {shown.map((option) => {
                const display = prescribedTherapyInputValue(option);
                return (
                  <CommandItem key={option.id} value={option.id} onSelect={() => commit(option)}>
                    <Check
                      className={[
                        "mr-2 h-3 w-3 shrink-0",
                        value === display ? "opacity-100" : "opacity-0",
                      ].join(" ")}
                      aria-hidden
                    />
                    <span className="flex min-w-0 flex-col">
                      <span className="text-dense font-semibold text-ink-text">{display}</span>
                      {option.label !== display && (
                        <span className="truncate text-mini text-ink-muted">{option.label}</span>
                      )}
                    </span>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export default PrescribedTherapySelect;
